/**
 * Native Haptic Feedback System
 * Utilise l'API Median Haptics (iOS/Android) avec fallback web
 * Respecte les préférences d'accessibilité et utilisateur
 */

import { isMedianNative, triggerMedianHaptic } from '@/utils/medianNativeBridge';

export type NativeHapticType =
  | 'selection'      // Sélection légère (iOS picker, navigation)
  | 'impact-light'   // Impact léger (boutons secondaires)
  | 'impact-medium'  // Impact moyen (boutons principaux)
  | 'impact-heavy'   // Impact fort (actions importantes)
  | 'success'        // Notification de succès
  | 'warning'        // Notification d'avertissement
  | 'error';         // Notification d'erreur

/**
 * Patterns de vibration pour fallback web (en millisecondes)
 */
const WEB_HAPTIC_PATTERNS: Record<NativeHapticType, number | number[]> = {
  selection: [5],
  'impact-light': [10],
  'impact-medium': [20],
  'impact-heavy': [30],
  success: [10, 50, 10],
  warning: [20, 50, 20],
  error: [30, 100, 30]
};

/**
 * Interface pour les diagnostics haptics
 */
export interface HapticDiagnostics {
  timestamp: string;
  isMedian: boolean;
  hasHapticsAPI: boolean;
  hasVibrate: boolean;
  reducedMotion: boolean;
  userDisabled: boolean;
  medianApis: string[];
  environment: 'native' | 'web' | 'unknown';
}

/**
 * Récupère les diagnostics complets du système haptic
 */
export const getHapticDiagnostics = (): HapticDiagnostics => {
  if (typeof window === 'undefined') {
    return {
      timestamp: new Date().toISOString(),
      isMedian: false,
      hasHapticsAPI: false,
      hasVibrate: false,
      reducedMotion: false,
      userDisabled: false,
      medianApis: [],
      environment: 'unknown'
    };
  }

  const isMedian = isMedianNative();
  const hasHapticsAPI = !!window.median?.haptics;
  
  return {
    timestamp: new Date().toISOString(),
    isMedian,
    hasHapticsAPI,
    hasVibrate: 'vibrate' in navigator,
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    userDisabled: localStorage.getItem('koken_haptics_disabled') === 'true',
    medianApis: window.median ? Object.keys(window.median) : [],
    environment: isMedian ? 'native' : 'web'
  };
};

/**
 * Vérifie si le haptic feedback est disponible et autorisé
 */
const isHapticEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Respecter prefers-reduced-motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return false;
  }

  // Vérifier les préférences utilisateur
  const disabled = localStorage.getItem('koken_haptics_disabled');
  return disabled !== 'true';
};

/**
 * Fallback web utilisant navigator.vibrate
 */
const fallbackWebHaptic = (type: NativeHapticType): void => {
  if (typeof window === 'undefined' || !('vibrate' in navigator)) return;

  try {
    const pattern = WEB_HAPTIC_PATTERNS[type];
    navigator.vibrate(pattern);
  } catch (error) {
    // Silent fail - web vibration not available
  }
};

export interface TriggerHapticOptions {
  /** Force le haptic même si désactivé par l'utilisateur (pour actions critiques) */
  force?: boolean;
  /** Identifiant pour les logs (aide au debug) */
  debugId?: string;
}

/**
 * Déclenche un retour haptique natif (Median iOS/Android) ou web
 * Note: iOS Safari ne supporte PAS navigator.vibrate - haptic uniquement via Median app
 * 
 * LOGS DE DIAGNOSTIC DÉTAILLÉS:
 * - Toujours loggés pour aider au debug mobile
 * - Format: [Haptic:ACTION] {...diagnostics}
 * 
 * @param type Type de haptic feedback
 * @param options Options de déclenchement (force: bypass les préférences utilisateur)
 */
export const triggerNativeHaptic = async (
  type: NativeHapticType = 'selection',
  options?: TriggerHapticOptions
): Promise<void> => {
  const isDev = import.meta.env.DEV;
  const diagnostics = getHapticDiagnostics();

  if (isDev) {
    const debugContext = {
      ...diagnostics,
      requestedType: type,
      force: options?.force ?? false,
      debugId: options?.debugId || 'unknown',
      callStack: new Error().stack?.split('\n').slice(1, 4).join(' <- ') || 'N/A'
    };
    console.log(`[Haptic:START] ${type}`, JSON.stringify(debugContext, null, 0));
  }

  if (!options?.force && !isHapticEnabled()) {
    if (isDev) {
      const blockReason = diagnostics.reducedMotion 
        ? 'prefers-reduced-motion' 
        : diagnostics.userDisabled 
          ? 'user-disabled-in-settings'
          : 'unknown-block-reason';
      console.log(`[Haptic:BLOCKED] ${blockReason} (${type})`);
    }
    return;
  }

  // =====================================================
  // EXECUTION: Tenter le haptic
  // =====================================================
  try {
    if (diagnostics.isMedian) {
      // ---- MODE MEDIAN NATIVE (iOS/Android) ----
      if (diagnostics.hasHapticsAPI && window.median?.haptics) {
        triggerMedianHaptic(type);
        if (isDev) console.log(`[Haptic:SUCCESS] ${type} via Median`);
      } else {
        if (isDev) console.warn(`[Haptic:WARN] Median detected but haptics API missing`);
        if (diagnostics.hasVibrate) {
          fallbackWebHaptic(type);
        }
      }
    } else {
      if (diagnostics.hasVibrate) {
        fallbackWebHaptic(type);
      } else if (isDev) {
        console.log(`[Haptic:UNAVAILABLE] No haptic API (${diagnostics.environment})`);
      }
    }
  } catch (error) {
    console.error(`[Haptic:ERROR] Exception during haptic trigger`, {
      type,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
  }
};

/**
 * Test direct de l'API Median (bypass tous les checks)
 * Utile pour diagnostiquer si l'API Median fonctionne
 */
export const testDirectMedianHaptic = (style: 'impactLight' | 'impactMedium' | 'impactHeavy' | 'notificationSuccess' | 'notificationWarning' | 'notificationError' = 'impactMedium'): boolean => {
  console.log('[Haptic:DIRECT_TEST] Starting direct Median test...', { style });
  
  if (typeof window === 'undefined') {
    console.log('[Haptic:DIRECT_TEST] Window undefined - SSR context');
    return false;
  }
  
  if (!window.median?.haptics) {
    console.log('[Haptic:DIRECT_TEST] No Median haptics API', {
      hasMedian: !!window.median,
      medianKeys: window.median ? Object.keys(window.median) : []
    });
    return false;
  }
  
  try {
    window.median.haptics.trigger({ style });
    console.log('[Haptic:DIRECT_TEST] ✅ Success - Median haptic triggered', { style });
    return true;
  } catch (error) {
    console.error('[Haptic:DIRECT_TEST] ❌ Failed', { error });
    return false;
  }
};

/**
 * Mapping de l'ancien système vers le nouveau (pour migration progressive)
 */
export const legacyToNativeHaptic = (
  legacyType: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning'
): NativeHapticType => {
  const mapping: Record<string, NativeHapticType> = {
    light: 'impact-light',
    medium: 'impact-medium',
    heavy: 'impact-heavy',
    success: 'success',
    error: 'error',
    warning: 'warning'
  };
  return mapping[legacyType] || 'selection';
};

/**
 * Active/désactive le haptic feedback avec sync Native Datastore
 */
export const setHapticEnabled = async (enabled: boolean): Promise<void> => {
  if (typeof window === 'undefined') return;
  
  const storageValue = enabled ? 'false' : 'true';
  
  // 1. Toujours sauvegarder dans localStorage (fallback web)
  localStorage.setItem('koken_haptics_disabled', storageValue);
  
  // 2. Si Median Native, sauvegarder aussi dans Native Datastore
  if (window.median?.storage?.app) {
    try {
      await window.median.storage.app.set({
        key: 'koken_haptics_disabled',
        value: storageValue
      });
      console.log('[Haptic:SETTINGS] Synced to Native Datastore');
    } catch (error) {
      console.warn('[Haptic:SETTINGS] Native Datastore sync failed:', error);
    }
  }
  
  console.log('[Haptic:SETTINGS] User preference updated', { enabled, storageValue });
};

/**
 * Initialise la préférence haptic depuis Native Datastore au démarrage
 * À appeler une fois au démarrage de l'app
 */
export const initHapticPreference = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  
  // Si Median Native, synchroniser depuis Native Datastore vers localStorage
  if (window.median?.storage?.app) {
    try {
      const result = await window.median.storage.app.get({ key: 'koken_haptics_disabled' });
      if (result?.data) {
        localStorage.setItem('koken_haptics_disabled', result.data);
        console.log('[Haptic:INIT] Synced from Native Datastore:', result.data);
      }
    } catch (error) {
      console.warn('[Haptic:INIT] Native Datastore read failed:', error);
    }
  }
};

/**
 * Vérifie si le haptic feedback est activé
 */
export const getHapticEnabled = (): boolean => {
  return isHapticEnabled();
};

/**
 * Types d'impact pour compatibilité avec l'ancien code
 */
export const ImpactStyle = {
  Light: 'impact-light' as NativeHapticType,
  Medium: 'impact-medium' as NativeHapticType,
  Heavy: 'impact-heavy' as NativeHapticType,
};

/**
 * Types de notification pour compatibilité avec l'ancien code
 */
export const NotificationType = {
  Success: 'success' as NativeHapticType,
  Warning: 'warning' as NativeHapticType,
  Error: 'error' as NativeHapticType,
};

/**
 * Objet Haptics compatible avec l'ancienne API
 * Permet une migration transparente
 */
export const Haptics = {
  impact: async ({ style }: { style: NativeHapticType }) => {
    await triggerNativeHaptic(style, { debugId: 'Haptics.impact' });
  },
  notification: async ({ type }: { type: NativeHapticType }) => {
    await triggerNativeHaptic(type, { debugId: 'Haptics.notification' });
  },
  selectionStart: async () => {
    await triggerNativeHaptic('selection', { debugId: 'Haptics.selectionStart' });
  },
  selectionChanged: async () => {
    await triggerNativeHaptic('selection', { debugId: 'Haptics.selectionChanged' });
  },
  selectionEnd: async () => {
    // No-op for end
  },
};
